import React, { useCallback, useMemo, useState } from 'react';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import styled from 'styled-components';
import { Col, Row } from 'antd';

import { OFFICES_URL_PREFIX, SITE_URL } from '../../../common/consts';
import Office from '../../../backend/s3/office';
import Flat from '../../../backend/s3/flat';
import {
  BuyHouseOption,
  computeBuyHouseOptions,
} from '../../../common/helpers/buyHouseOption.utils';
import {
  getCapitalCitiesQueries,
  getOfficesByCountyPageQueries,
} from '../../../common/helpers/i18n.utils';
import {
  computeOfficesByCityArray,
  computeOfficeSearchOptions,
  getOfficesByCountyAndCity,
} from '../../../common/helpers/office.utils';
import { slugifySearchQuery } from '../../../common/helpers/searchQuery.utils';
import { generateOfficeListPageStructuredData } from '../../../common/helpers/structuredData.utils';
import useI18n from '../../../common/hooks/useI18n';
import { IOffice } from '../../../common/model/office.model';

import {
  LocationTitle,
  OfficesBreadcrumb,
  OfficesMap,
  OfficesSearchHero,
} from '../../../components/offices';
import {
  Footer,
  Head,
  Header,
  OfficesRowContainer,
  TextSection,
} from '../../../components/shared';

type StaticProps = {
  offices: IOffice[];
  buyHouseOptions: BuyHouseOption[];
  query: string;
  filteredOffices: IOffice[];
};

type Props = StaticProps;

const Layout = styled.div`
  display: flex;
  flex: auto;
  flex-direction: column;
  box-sizing: border-box;
  min-height: 0;

  font-style: ${(props) => props.theme.font.style};
`;

const Content = styled.main`
  position: relative;
  flex: auto;
  margin-top: ${(props) => props.theme.headerHeight};

  @media ${(props) => props.theme.breakpoints.mdd} {
    margin-top: 0;
  }
`;

const HeroBottomDivider = styled.div`
  padding-top: 3rem;

  @media ${(props) => props.theme.breakpoints.smd} {
    padding-top: 2rem;
  }
`;

const BreadcrumbContainer = styled.div`
  margin-bottom: -1rem;
`;

const ContentWithPadding = styled.div`
  padding-left: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  padding-right: ${(props) => props.theme.grid.getGridColumns(2, 1)};
`;

const ContentWithMarginTop = styled.div`
  position: relative;
  margin-top: 3rem;

  @media ${(props) => props.theme.breakpoints.smd} {
    margin-top: 2rem;
  }
`;

const TextContent = styled.p`
  ${(props) => props.theme.font.p1};
  color: ${(props) => props.theme.colors.secondary};
  white-space: pre-wrap;
  margin-top: 2rem;
`;

const OfficesByCountyPage: NextPage<Props> = ({
  offices,
  buyHouseOptions,
  query,
  filteredOffices,
}) => {
  const i18n = useI18n();

  const [autoCompleteValue, setAutoCompleteValue] = useState('');

  const structuredData = useMemo(
    () => generateOfficeListPageStructuredData(offices),
    [offices]
  );

  const countyFromQuery = useMemo(() => query.split('/')[0], [query]);
  const cityFromQuery = useMemo(() => query.split('/')[1], [query]);
  const countyName = useMemo(
    () => filteredOffices[0].province || countyFromQuery.split('-')[0],
    [countyFromQuery, filteredOffices]
  );
  const cityName = useMemo(() => cityFromQuery && filteredOffices[0].city, [
    cityFromQuery,
    filteredOffices,
  ]);
  const options = useMemo(
    () => computeOfficeSearchOptions(filteredOffices, !cityFromQuery),
    [cityFromQuery, filteredOffices]
  );
  const officesByCityArray = useMemo(
    () => computeOfficesByCityArray(filteredOffices),
    [filteredOffices]
  );

  const getQuery = useCallback(
    (city) => `${countyFromQuery}/${slugifySearchQuery(city)}`,
    [countyFromQuery]
  );

  const shouldShowCity = useCallback(
    (city: string) => {
      const cityMetaTitle = i18n.t(
        `dynamic.officesByCountyPage.${getQuery(city)}.metaTitle`
      );
      return !!cityMetaTitle;
    },
    [getQuery, i18n]
  );

  const getIndependentAndOtherCities = useCallback(() => {
    const independent = officesByCityArray.filter((o) => shouldShowCity(o[0]));
    const other = officesByCityArray.filter((o) => !shouldShowCity(o[0]));
    return [independent, other];
  }, [officesByCityArray, shouldShowCity]);

  const independentOfficesByCityArray = useMemo(
    () => getIndependentAndOtherCities()[0],
    [getIndependentAndOtherCities]
  );

  const otherOffices = useMemo(() => {
    const other = getIndependentAndOtherCities()[1];
    return other.reduce((acc, cur) => {
      acc.push(...cur[1]);
      return acc;
    }, []);
  }, [getIndependentAndOtherCities]);

  const isCapitalCity = useCallback(
    (city: string) =>
      slugifySearchQuery(
        i18n.t(`dynamic.officesByCountyPage.${countyFromQuery}.capitalCity`)
      ) === slugifySearchQuery(city),
    [countyFromQuery, i18n]
  );

  return (
    <Layout>
      <Head
        title={i18n.t(`dynamic.officesByCountyPage.${query}.metaTitle`)}
        description={i18n.t(
          `dynamic.officesByCountyPage.${query}.metaDescription`
        )}
        canonicalUrl={`${SITE_URL}/${OFFICES_URL_PREFIX}/${query}`}
      >
        {structuredData && (
          <script
            id="structured-data-script"
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
          />
        )}
      </Head>

      <Header offices={offices} buyHouseOptions={buyHouseOptions} />

      <Content>
        <OfficesSearchHero
          autoCompleteValue={autoCompleteValue}
          autoCompleteOptions={options.filter((o) =>
            o.text
              .toLowerCase()
              .includes(autoCompleteValue.toLowerCase().trim())
          )}
          groupedByCity={true}
          onAutoCompleteValueChange={setAutoCompleteValue}
          imageUrl={i18n.stagingT(
            `dynamic.officesByCountyPage.${query}.heroImageUrl`
          )}
          i18nKeyPrefix={`dynamic.officesByCountyPage.${query}.`}
        />
        <HeroBottomDivider />

        <ContentWithPadding>
          <BreadcrumbContainer>
            <OfficesBreadcrumb offices={offices} />
          </BreadcrumbContainer>

          <ContentWithMarginTop>
            <Row gutter={[32, 32]} align="top">
              <Col xs={24} lg={8}>
                <TextSection
                  title={i18n.t(
                    `dynamic.officesByCountyPage.${query}.descriptionTitle`
                  )}
                  content={i18n.t(
                    `dynamic.officesByCountyPage.${query}.descriptionText`
                  )}
                />
              </Col>
              <Col xs={24} lg={16}>
                {typeof google !== 'undefined' &&
                  typeof google.maps.Map !== 'undefined' && (
                    <OfficesMap offices={filteredOffices} />
                  )}
              </Col>
            </Row>
          </ContentWithMarginTop>
        </ContentWithPadding>

        <ContentWithMarginTop>
          {independentOfficesByCityArray.map((officesByCity) => (
            <div key={officesByCity[0]}>
              <ContentWithPadding>
                <LocationTitle
                  query={getQuery(officesByCity[0])}
                  location={officesByCity[0]}
                  disableImage={cityName === officesByCity[0]}
                  disableHref={isCapitalCity(officesByCity[0])}
                />
                {isCapitalCity(officesByCity[0]) && (
                  <TextContent
                    key={officesByCity[0] + new Date().getTime()}
                    as="p"
                    dangerouslySetInnerHTML={{
                      __html: i18n.t(
                        `dynamic.officesByCountyPage.${getQuery(
                          officesByCity[0]
                        )}.descriptionText`
                      ),
                    }}
                  />
                )}
              </ContentWithPadding>
              <OfficesRowContainer offices={officesByCity[1]} />
            </div>
          ))}

          {otherOffices.length > 0 && (
            <>
              <ContentWithPadding>
                <LocationTitle
                  query={null}
                  location={
                    independentOfficesByCityArray.length === 0
                      ? cityName || countyName
                      : i18n.t('offices.otherCities')
                  }
                  disableHref
                />
              </ContentWithPadding>
              <OfficesRowContainer offices={otherOffices} />
            </>
          )}
        </ContentWithMarginTop>
      </Content>

      <Footer />
    </Layout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const offices = await Office.getOffices();
  const queries = getOfficesByCountyPageQueries(offices);

  const capitalCitiesQueries = getCapitalCitiesQueries(queries);

  let filteredQueries = offices
    .map((o) => {
      const matchingQuery = queries.find((q) => {
        const splitQ = q.split('/');
        if (splitQ.length > 1) {
          return (
            splitQ[0].includes(slugifySearchQuery(o.province)) &&
            splitQ[1].includes(slugifySearchQuery(o.city))
          );
        }
        return splitQ[0].includes(slugifySearchQuery(o.province));
      });
      if (!matchingQuery) {
        return null;
      }
      return matchingQuery.replaceAll('/', '--');
    })
    .filter((p) => !!p)
    .filter((p) => !capitalCitiesQueries.includes(p.replaceAll('--', '/')));
  filteredQueries = Array.from(new Set(filteredQueries));

  return {
    paths: filteredQueries.map((query) => ({ params: { query } })),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<StaticProps> = async ({
  params,
}) => {
  const offices = await Office.getOffices();
  const [_, searchOptions] = await Flat.getFlats();

  const buyHouseOptions = computeBuyHouseOptions(searchOptions);

  const query = (params.query as string).replaceAll('--', '/');
  const countyFromQuery = query.split('/')[0];
  const cityFromQuery = query.split('/')[1];
  const filteredOffices = getOfficesByCountyAndCity(
    offices,
    countyFromQuery,
    cityFromQuery
  );

  return {
    props: {
      offices,
      buyHouseOptions,
      query,
      filteredOffices,
    },
  };
};

export default OfficesByCountyPage;
